import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { clearModuleResponseLocalStorage, resetUserModuleProgress } from '../../services/lmsData';
import './Header.css';
import lotusLogo from '../../Logo.png';
import ckLogo from '../../CK-logo.png';

const Header = ({ user, onLogout, onToggleSidebar = () => {} }) => {
  const { firebaseUser, refreshModuleProgress } = useAuth();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [resettingProgress, setResettingProgress] = useState(false);
  const location = useLocation();

  const toggleUserMenu = () => {
    setShowUserMenu(!showUserMenu);
  };

  const handleResetProgress = async () => {
    if (!firebaseUser?.uid || resettingProgress) return;
    const confirmed = window.confirm(
      'Reset all module progress and submitted responses for this account? This removes Firestore progress, surveys, and other module submissions linked to you.'
    );
    if (!confirmed) return;

    setResettingProgress(true);
    try {
      await resetUserModuleProgress(firebaseUser.uid);
      clearModuleResponseLocalStorage();
      await refreshModuleProgress?.();
      setShowUserMenu(false);
    } finally {
      setResettingProgress(false);
    }
  };
  
  const isActive = (path) => {
    return location.pathname === path;
  };

  return (
    <header className="header" role="banner">
      <div className="header-content">
        <div className="header-left">
          <button className="menu-toggle" onClick={onToggleSidebar} aria-label="Toggle navigation menu">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16"/>
            </svg>
          </button>
        </div>

        <div className="logo-container">
          <div className="logo-section">
            <div className="logo-partners">
              <Link to="/" className="partner-logo-link" aria-label="Lotus STEMM home">
                <img
                  src={lotusLogo}
                  alt="Lotus STEMM Logo"
                  className="partner-logo"
                />
              </Link>
              <span className="logo-divider">×</span>
              <a
                href="https://campfirekinship.com/"
                target="_blank"
                rel="noopener noreferrer"
                className="partner-logo-link"
                aria-label="Campfire Kinship (opens in a new tab)"
              >
                <img
                  src={ckLogo}
                  alt="Campfire Kinship Logo"
                  className="partner-logo"
                />
              </a>
            </div>
            <Link to="/" className="logo-subtitle">
              Assume We Can: Break Barriers, Build Opportunities
            </Link>
          </div>
        </div>

        <div className="header-right">
          {/* <div className="search-container">
            <svg className="search-icon" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor">
              <circle cx="11" cy="11" r="8"/>
              <path d="m21 21-4.35-4.35"/>
            </svg>
            <input 
              type="text" 
              placeholder="Search modules, resources..." 
              className="search-input"
            />
          </div> */}

          <button className="notification-btn" aria-label="Notifications - 3 unread">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"/>
            </svg>
            <span className="notification-badge" aria-hidden="true">0</span>
          </button>

          <div className="user-menu-container">
            <button className="user-avatar" onClick={toggleUserMenu} aria-label="User menu" aria-expanded={showUserMenu}>
              <div className="avatar-circle">
                {user.avatar ? (
                  <img src={user.avatar} alt={user.name} />
                ) : (
                  <span aria-hidden="true">{user.name.split(' ').map(n => n[0]).join('')}</span>
                )}
              </div>
              <span className="user-name">{user.name.split(' ')[0]}</span>
              <svg className="chevron" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"/>
              </svg>
            </button>

            {showUserMenu && (
              <div className="user-dropdown">
                <div className="user-info">
                  <div className="avatar-circle large">
                    {user.avatar ? (
                      <img src={user.avatar} alt={user.name} />
                    ) : (
                      <span>{user.name.split(' ').map(n => n[0]).join('')}</span>
                    )}
                  </div>
                  <div className="user-details">
                    <div className="user-dropdown-name">{user.name}</div>
                    <div className="user-dropdown-email">{user.email}</div>
                  </div>
                </div>
                <div className="dropdown-divider"></div>
                <Link to="/profile" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"/>
                  </svg>
                  My Profile
                </Link>
                <Link to="/settings" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"/>
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                  </svg>
                  Settings
                </Link>
                {/* <Link to="/feedback" className="dropdown-item" onClick={() => setShowUserMenu(false)}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M7 8h10M7 12h4m1 8l-4-4H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-3l-4 4z"/>
                  </svg>
                  Feedback
                </Link> */}
                <div className="dropdown-divider"></div>
                {/* Data export hidden from menus - open /admin/data-export directly when needed */}
                <button
                  type="button"
                  className="dropdown-item dropdown-item--reset-progress"
                  onClick={handleResetProgress}
                  disabled={resettingProgress || !firebaseUser?.uid}
                  title="Reset this user's module progress"
                >
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                  </svg>
                  {resettingProgress ? 'Resetting…' : 'Reset My Progress'}
                </button>
                <div className="dropdown-divider"></div>
                <button className="dropdown-item logout" onClick={onLogout}>
                  <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" aria-hidden="true">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"/>
                  </svg>
                  Logout
                </button>
              </div>
            )}
          </div>
          </div>
        </div>
        
        {/* Horizontal Navigation Menu */}
        <nav className="horizontal-nav" role="navigation" aria-label="Main navigation">
          <div className="horizontal-nav-content">
            <Link 
              to="/" 
              className={`nav-link ${isActive('/') ? 'active' : ''}`}
            >
              Home
            </Link>
            <span className="nav-separator">|</span>
            
            <Link 
              to="/modules" 
              className={`nav-link ${isActive('/modules') ? 'active' : ''}`}
            >
              My Modules
            </Link>
            <span className="nav-separator">|</span>
            
            <Link 
              to="/resources" 
              className={`nav-link ${isActive('/resources') ? 'active' : ''}`}
            >
              Resources
            </Link>
            <span className="nav-separator">|</span>
            
            <Link
              to="/profile"
              className={`nav-link ${isActive('/profile') && location.hash !== '#certificates' ? 'active' : ''}`}
            >
              My Profile
            </Link>
            <span className="nav-separator">|</span>

            <Link
              to="/profile#certificates"
              className={`nav-link ${location.pathname === '/profile' && location.hash === '#certificates' ? 'active' : ''}`}
            >
              Certificates
            </Link>
            <span className="nav-separator">|</span>

            <Link
              to="/about"
              className={`nav-link ${isActive('/about') ? 'active' : ''}`}
            >
              About
            </Link>
            {/* Data export hidden from top menu - open /admin/data-export directly when needed */}
            {/* <span className="nav-separator">|</span> */}
            
            {/* <a href="#forum" className="nav-link">
              Discussion Forum
            </a>
            <span className="nav-separator">|</span> */}
            
            {/* <a href="#help" className="nav-link">
              Help & Support
            </a> */}
          </div>
        </nav>
      </header>
    );
  };
  
  export default Header;

